
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { CrudFormConfig, CrudFormItem, CrudViewFormItemType } from '../../utils/edit-form';
import { orderBy, groupBy } from 'lodash';
import { CrudFormSection } from '../../utils/edit-form/crud-form-section.interface';
import { TextColorType } from '../../utils/edit-form/crud-view-text-color-type.enum';
import { TextStyleType } from '../../utils/edit-form/crud-view-text-style-type.enum';
import { EntityConfigInterface } from '../crud-view/model/entity';
import { TypographyConfig } from '../../utils/shared/typography/typography.interface';
import { ColorConfig } from '../../utils/shared/color/color.interface';
import { FontWeight } from '../../utils/shared/typography/font-weight.enum';
import { FontStyle } from '../../utils/shared/typography/font-style.enum';
import { TextDecoration } from '../../utils/shared/typography/text-decoration.enum';

@Component({
  selector: 'rappider-crud-fields-display',
  templateUrl: './crud-fields-display.component.html',
  styleUrls: ['./crud-fields-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderCrudFieldsDisplayComponent implements OnInit, OnChanges {
  @Input() data: any | undefined;
  @Input() config: CrudFormConfig | undefined;
  @Input() entityConfig?: EntityConfigInterface | undefined;
  /* Displayer is on edit mode, when the editMode is true, elements will show inputs */
  @Input() editMode?: boolean = false;
  /* Custom style class for collapse panel */
  @Input() collapsePanelCustomClass?: string;
  @Input() collapsePanelsActive?: boolean = true;

  /* Number of columns to display for sections */
  @Input() columnNumber?: number = 2;

  @Input() collapseExtra?: TemplateRef<any>;

  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<boolean>();
  @Output() collapseActiveChange = new EventEmitter<{ isActive: boolean; collapseSection: any }>();

  /* Form items grouped by sections */
  groupedItems;
  /* names of the sections */
  itemSections: CrudFormSection[];

  CrudViewFormItemType = CrudViewFormItemType;

  itemsDoNotShowAsLabel = [
    CrudViewFormItemType.Table,
    CrudViewFormItemType.CheckBox,
    CrudViewFormItemType.MarkDown,
  ];

  TextColorType = TextColorType;
  TextStyleType = TextStyleType;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.prepareConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] || changes['columnNumber']) {
      this.prepareConfig();
      this.cdr.markForCheck();
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    }
    if (changes['config'] && changes['config'].currentValue) {
      const prevTitle = changes['config'].previousValue?.defaultSectionTitle;
      const currTitle = changes['config'].currentValue?.defaultSectionTitle;
      const prevShowInCollapsed = changes['config'].previousValue?.showInCollapsedSections;
      const currShowInCollapsed = changes['config'].currentValue?.showInCollapsedSections;
      if (prevTitle !== currTitle || prevShowInCollapsed !== currShowInCollapsed) {
        this.prepareConfig();
        this.cdr.markForCheck();
      }
    }
  }

  prepareConfig() {
    this.groupedItems = {};
    this.itemSections = [];
    if (!this.config?.items || this.config.items.length === 0) {
      return;
    }
    let visibleItems = this.config.items.filter(item => item.visible !== false);

    const itemsWithIndex = visibleItems.some(item => item.index !== undefined);
    if (itemsWithIndex) {
      visibleItems = orderBy(visibleItems, 'index', 'asc');
    }
    // If no index property, maintain the array order as-is
    if (visibleItems.some(item => item.section)) {
      visibleItems = visibleItems.map(item => ({
        ...item,
        section: item.section || 'Other'
      }));
      this.groupedItems = groupBy(visibleItems, 'section');

      if (this.config?.sections?.length) {
        this.itemSections = Object.keys(this.groupedItems).filter(i => i).map(sectionName => {
          const normalizedSectionName = sectionName.toLowerCase().replace(/\s+/g, '');
          const configSection = this.config.sections.find(s => {
            const normalizedKey = s.key?.toLowerCase().replace(/\s+/g, '') || '';
            const normalizedTitle = s.title?.toLowerCase().replace(/\s+/g, '') || '';
            return s.key === sectionName ||
                   normalizedKey === normalizedSectionName ||
                   s.title === sectionName ||
                   normalizedTitle === normalizedSectionName;
          });
          const defaultTitle = this.config?.defaultSectionTitle?.trim();
          const sectionTitle = (defaultTitle && defaultTitle.length > 0)
                               ? defaultTitle
                               : (configSection?.title?.trim() || sectionName);
            return <CrudFormSection>{
              title: sectionTitle,
              key: sectionName, // CRITICAL: Always use sectionName as key to match groupedItems
              columns: configSection?.columns || this.columnNumber,
              titleType: configSection?.titleType
            };
        });
      } else {
        this.itemSections = Object.keys(this.groupedItems).filter(i => i).map(sectionName =>
          <CrudFormSection>{
            title: this.config?.defaultSectionTitle || sectionName,
            key: sectionName, // CRITICAL: Always use sectionName as key to match groupedItems
            columns: this.columnNumber
          });
      }
    } else {
      if (this.config?.sections?.length) {
        const defaultTitle = this.config?.defaultSectionTitle?.trim();
          this.itemSections = this.config.sections.map(section => ({
            ...section,
            title: (defaultTitle && defaultTitle.length > 0)
                   ? defaultTitle
                   : (section.title || section.key || '')
          }));
      } else {
        this.itemSections = [<CrudFormSection>{
          title: this.config?.defaultSectionTitle || '',
          key: '',
          columns: this.columnNumber
        }];
      }
      this.groupedItems = groupBy(visibleItems, 'section');
    }
  }

  getItemSize(itemSection: CrudFormSection) {
    if (this.config?.displayColumnNumberSelectorForDetailView) {
      return `calc(${100 / this.columnNumber}% - 10px)`;
    } else {
      return itemSection?.columns ? `calc(${100 / itemSection?.columns}% - 10px)` : `calc(${100 / this.columnNumber}% - 10px)`;
    }
  }

  onFieldValueChange(event: any) {
    console.log('Field value changed:', event);
  }

  onFormSubmit(event: any) {
    console.log('Form submitted', event);
    this.formSubmit.emit(event);
  }

  onFormCancel() {
    console.log('Form cancelled');
    this.formCancel.emit(true);
  }

  getTitleWithHeadingType(titleType: string, title: string) {
    return `<${titleType}>${title}</${titleType}>`;
  }

  onCollapseActiveChange(event, itemSection) {
    this.collapseActiveChange.emit({ isActive: event, collapseSection: itemSection });
    console.log('Collapse active change:', event, itemSection);
  }

  isObject(value: any): boolean {
    return typeof value === 'object' && value !== null;
  }

  /**
   * Get CSS classes for collapse panel
   */
  getCollapsePanelClasses(itemSection: CrudFormSection): string {
    const classes: string[] = [];
    if (this.collapsePanelCustomClass) {
      classes.push(this.collapsePanelCustomClass);
    }
    return classes.join(' ');
  }

  /**
   * Get CSS classes for field label
   */
  getFieldLabelClasses(field: CrudFormItem): string {
    const classes: string[] = [];
    if (field?.textColor) {
      classes.push(`field-color-${field.textColor}`);
    }
    if (field?.textStyle) {
      classes.push(`field-style-${field.textStyle}`);
    }
    return classes.join(' ');
  }

  /**
   * Get text color CSS variable value
   * Supports both TextColorType enum and hex color strings
   */
  getTextColorValue(colorType: TextColorType | string | undefined): string {
    if (!colorType || colorType === TextColorType.Default) {
      return '';
    }
    // If it's a hex color string, return it directly
    if (typeof colorType === 'string' && colorType.startsWith('#')) {
      return colorType;
    }
    // Otherwise, map enum values to CSS variables
    const colorMap = {
      [TextColorType.Warning]: 'var(--warning-color)',
      [TextColorType.Error]: 'var(--danger-color)',
      [TextColorType.Success]: 'var(--success-color)',
      [TextColorType.Info]: 'var(--info-color)'
    };
    return colorMap[colorType as TextColorType] || '';
  }

  /**
   * Get background color value from cssStyle
   * For field labels, uses 'label-background-color'
   * For field values, uses 'background-color'
   */
  getBackgroundColorValue(field: CrudFormItem | undefined): string {
    if (!field?.cssStyle) {
      return '';
    }
    // Field labels use label-background-color
    return field.cssStyle['label-background-color'] || '';
  }

  /**
   * Get color settings for rappider-text component
   * Supports both TextColorType enum and hex color strings
   */
  getValueColorSettings(colorType: TextColorType | string | undefined, field?: CrudFormItem): ColorConfig | undefined {
    const color = colorType && colorType !== TextColorType.Default ? this.getTextColorValue(colorType) : undefined;
    const backgroundColor = field?.cssStyle ? (field.cssStyle['background-color'] || field.cssStyle['backgroundColor']) : undefined;

    if (!color && !backgroundColor) {
      return undefined;
    }

    return {
      color,
      backgroundColor
    };
  }

  /**
   * Get typography settings for rappider-text component
   */
  getValueTypographySettings(styleType: TextStyleType | undefined): TypographyConfig | undefined {
    if (!styleType) {
      return undefined;
    }
    const settings: TypographyConfig = {};

    if (styleType === TextStyleType.Bold || styleType === TextStyleType.BoldItalic) {
      settings.fontWeight = FontWeight.Bold;
    }
    if (styleType === TextStyleType.Italic || styleType === TextStyleType.BoldItalic) {
      settings.fontStyle = FontStyle.Italic;
    }
    if (styleType === TextStyleType.Underline) {
      settings.textDecoration = TextDecoration.Underline;
    }

    return Object.keys(settings).length > 0 ? settings : undefined;
  }

  /**
   * Get text style CSS value
   */
  getTextStyleValue(styleType: TextStyleType | undefined): string {
    if (!styleType || styleType === TextStyleType.Normal) {
      return '';
    }
    const styleMap = {
      [TextStyleType.Bold]: 'bold',
      [TextStyleType.Italic]: 'italic',
      [TextStyleType.Underline]: 'underline',
      [TextStyleType.BoldItalic]: 'bold italic'
    };
    return styleMap[styleType] || '';
  }
}
